import type { CallbackInterface } from "recoil";

import { logAppError } from "Helpers/logger";
import { errorUpdater, loaderUpdater, pageState } from "State/page";

import type { ModalAddSegmentState } from "Pages/RecipientsList/model";
import {
  isGroupNameAvailable,
  segmentRecipients,
} from "Pages/RecipientsList/recoil/services/modalAddSegment";
import { notifyCreateSegmentSuccess } from "Pages/RecipientsList/recoil/services/notifications";
import { appliedSearchFiltersState } from "Pages/RecipientsList/recoil/state/filters";
import { modalAddSegmentState } from "Pages/RecipientsList/recoil/state/modalAddSegment";
import { paginationState } from "Pages/RecipientsList/recoil/state/results";
import { gainsightTrack } from "mailup-react-helpers";

export const modalAddSegmentOpenCallback =
  ({ set, snapshot }: CallbackInterface) =>
  async () => {
    gainsightTrack("mup_recipients_click_createSegmentButton", {});
    set(pageState, loaderUpdater(true));

    const searchCrit = await snapshot.getPromise(appliedSearchFiltersState);

    const { totalItems } = await snapshot.getPromise(paginationState);

    try {
      set(modalAddSegmentState, (prev) => ({
        ...prev,
        show: true,
        recipientsCount: totalItems,
        searchCrit,
      }));
    } catch (error) {
      console.error("### error", error);

      logAppError({
        Component: "RecipientsList",
        Method: "modalAddSegmentOpenCallback",
        appError: error,
      });

      set(pageState, errorUpdater);
    }

    set(pageState, loaderUpdater(false));
  };

export const modalAddSegmentChangeCallback =
  ({ set }: CallbackInterface) =>
  async (p: Partial<ModalAddSegmentState>) => {
    set(modalAddSegmentState, (prev) => ({ ...prev, ...p }));
  };

export const modalAddSegmentValidateNameCallback =
  ({ snapshot, set, reset }: CallbackInterface) =>
  async () => {
    const { name } = await snapshot.getPromise(modalAddSegmentState);

    try {
      const available = await isGroupNameAvailable(name);

      set(modalAddSegmentState, (prev) => ({
        ...prev,
        nameConflict: !available,
      }));
    } catch (error) {
      console.error("### error", error);

      logAppError({
        Component: "RecipientsList",
        Method: "modalAddSegmentValidateNameCallback",
        appError: error,
      });

      set(pageState, errorUpdater);

      reset(modalAddSegmentState);
    }
  };

export const modalAddSegmentCancelCallback =
  ({ reset }: CallbackInterface) =>
  async () => {
    reset(modalAddSegmentState);
  };

export const modalAddSegmentConfirmCallback =
  ({ set, reset, snapshot }: CallbackInterface) =>
  async () => {
    set(pageState, loaderUpdater(true));
    gainsightTrack("mup_recipients_click_confirmationCreateSegment", {});

    try {
      const m = await snapshot.getPromise(modalAddSegmentState);

      await segmentRecipients(m);

      notifyCreateSegmentSuccess(m.name);
    } catch (error) {
      console.error("### error", error);

      logAppError({
        Component: "RecipientsList",
        Method: "modalAddSegmentConfirmCallback",
        appError: error,
      });

      set(pageState, errorUpdater);
    }

    reset(modalAddSegmentState);

    set(pageState, loaderUpdater(false));
  };
